import { DatabaseBlockModel } from '@blocksuite/affine-model';
import {
  generateFractionalIndexingKeyBetween,
  uploadBlob,
} from '@blocksuite/affine-shared/utils';
import { assertDatabaseColumnShapeWithinLimits } from '@blocksuite/data-view';
import type { BlockStdScope } from '@blocksuite/std';
import type { Store } from '@blocksuite/store';

import {
  createDatabaseBlockDataSource,
  DatabaseBlockDataSource,
} from './data-source';

/**
 * Blob metadata for creating an attachment cell in a database row.
 */
export interface AttachmentBlobInfo {
  blobId: string;
  name: string;
  mime: string;
  /** Byte size from `File.size`. Zero is only valid for an empty file. */
  size: number;
}

/**
 * Creates a new row in the database with the given blob as attachment.
 * Finds or creates an attachment column if none exists.
 *
 * @returns The row ID of the newly created row, or empty string on failure.
 */
export function addRowWithAttachment(
  store: Store,
  dbModel: DatabaseBlockModel,
  blob: AttachmentBlobInfo
): string {
  const dataSource = createDatabaseBlockDataSource(dbModel);
  assertCanAppendAttachmentRows(dbModel, 1, 'Database attachment import');

  // Find existing or create new attachment column
  let attachmentPropId: string | null = findAttachmentColumn(dataSource);
  if (!attachmentPropId) {
    const propertyIds = dataSource.getAllPropertyIds();
    attachmentPropId =
      dataSource.propertyAdd(
        {
          id: propertyIds.at(-1) ?? '',
          before: false,
        },
        { type: 'attachment', name: 'Attachments' }
      ) ?? null;
  }
  if (!attachmentPropId) return '';

  // Create new row at end
  const rowId = dataSource.rowAdd(dataSource.rowCount$.value);

  if (dataSource.isRowSharded) {
    dataSource.cellValueChange(rowId, 'title', blob.name);
  } else {
    // Set title cell to filename
    const titleModel = store.getBlock(rowId)?.model;
    if (titleModel && 'text' in titleModel.props && titleModel.props.text) {
      (titleModel.props.text as { insert: (s: string) => void }).insert(
        blob.name
      );
    }
  }

  // Set attachment cell value. Persist size/mime on the column so downstream
  // runners (e.g. AIStepExecutor → SttBatchRunner) don't have to round-trip
  // the blob metadata out of the blobs table on every execution.
  const order = generateFractionalIndexingKeyBetween(null, null);
  dataSource.cellValueChange(rowId, attachmentPropId, {
    [blob.blobId]: {
      id: blob.blobId,
      name: blob.name,
      mime: blob.mime,
      size: blob.size,
      order,
    },
  });

  return rowId;
}

export function assertCanAppendAttachmentRows(
  dbModel: DatabaseBlockModel,
  rowCountToAppend: number,
  source: string
): void {
  if (!Number.isInteger(rowCountToAppend) || rowCountToAppend < 0) {
    throw new RangeError('Attachment row count must be a non-negative integer');
  }
  const dataSource = createDatabaseBlockDataSource(dbModel);

  const propertyIds = dataSource.getAllPropertyIds();
  const hasAttachmentColumn = propertyIds.some(
    propId => dataSource.propertyTypeGet(propId) === 'attachment'
  );

  assertDatabaseColumnShapeWithinLimits({
    source,
    columnCount: dataSource.columnCount$.value + (hasAttachmentColumn ? 0 : 1),
  });
}

function findAttachmentColumn(
  dataSource: DatabaseBlockDataSource
): string | null {
  for (const propId of dataSource.getAllPropertyIds()) {
    if (dataSource.propertyTypeGet(propId) === 'attachment') return propId;
  }
  return null;
}

/**
 * Uploads a File to the blob store and returns its metadata.
 * Accepts BlockStdScope to enable tus direct upload when available.
 */
export async function uploadFileAsBlob(
  std: BlockStdScope,
  file: File
): Promise<AttachmentBlobInfo> {
  const blobId = await uploadBlob(std, file);
  return {
    blobId,
    name: file.name,
    mime: file.type || 'application/octet-stream',
    size: file.size,
  };
}
